import React, { useState, useEffect } from 'react';
import { getSessions, addSession, updateSession, deleteSession, exportSessionsAsMarkdown, type WorkSession } from '../../utils/sessionStorage';
import { Clock, Plus, Edit2, Trash2, Download, CheckCircle, Circle } from 'lucide-react';
import toast from 'react-hot-toast';

export const SessionTracker: React.FC = () => {
    const [sessions, setSessions] = useState<WorkSession[]>([]);
    const [isCreating, setIsCreating] = useState(false);
    const [editingId, setEditingId] = useState<string | null>(null);
    const [formData, setFormData] = useState({
        objectives: '',
        notes: '',
        tasksCompleted: '',
        duration: '',
    });

    useEffect(() => {
        loadSessions();
    }, []);

    const loadSessions = () => {
        setSessions(getSessions());
    };

    const handleCreate = () => {
        if (!formData.objectives.trim()) {
            toast.error('Veuillez ajouter au moins un objectif');
            return;
        }

        const newSession = addSession({
            date: new Date().toISOString(),
            objectives: formData.objectives.split('\n').filter(o => o.trim()),
            notes: formData.notes,
            tasksCompleted: formData.tasksCompleted.split('\n').filter(t => t.trim()),
            duration: formData.duration ? parseInt(formData.duration) : undefined,
        });

        toast.success('Session créée !');
        resetForm();
        loadSessions();
    };

    const handleUpdate = (id: string) => {
        updateSession(id, {
            objectives: formData.objectives.split('\n').filter(o => o.trim()),
            notes: formData.notes,
            tasksCompleted: formData.tasksCompleted.split('\n').filter(t => t.trim()),
            duration: formData.duration ? parseInt(formData.duration) : undefined,
        });

        toast.success('Session mise à jour !');
        setEditingId(null);
        resetForm();
        loadSessions();
    };

    const handleDelete = (id: string) => {
        if (window.confirm('Supprimer cette session ?')) {
            deleteSession(id);
            toast.success('Session supprimée');
            loadSessions();
        }
    };

    const handleExport = () => {
        const markdown = exportSessionsAsMarkdown();
        const blob = new Blob([markdown], { type: 'text/markdown' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `sessions_${new Date().toISOString().split('T')[0]}.md`;
        a.click();
        toast.success('Export réussi !');
    };

    const startEdit = (session: WorkSession) => {
        setEditingId(session.id);
        setFormData({
            objectives: session.objectives.join('\n'),
            notes: session.notes,
            tasksCompleted: session.tasksCompleted.join('\n'),
            duration: session.duration?.toString() || '',
        });
    };

    const resetForm = () => {
        setFormData({ objectives: '', notes: '', tasksCompleted: '', duration: '' });
        setIsCreating(false);
        setEditingId(null);
    };

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex justify-between items-center">
                <h2 className="text-2xl font-serif font-bold flex items-center gap-2" style={{ color: 'var(--accent-glow)' }}>
                    <Clock className="w-6 h-6" />
                    Sessions de Travail
                </h2>
                <div className="flex gap-2">
                    <button
                        onClick={handleExport}
                        disabled={sessions.length === 0}
                        className="px-4 py-2 text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                        style={{ backgroundColor: '#3b82f6' }}
                    >
                        <Download className="w-4 h-4" />
                        Exporter
                    </button>
                    <button
                        onClick={() => setIsCreating(true)}
                        className="px-4 py-2 text-navy-900 rounded-lg font-bold transition-colors flex items-center gap-2"
                        style={{
                            backgroundColor: 'var(--accent-color)',
                            color: 'var(--bg-primary)'
                        }}
                    >
                        <Plus className="w-4 h-4" />
                        Nouvelle Session
                    </button>
                </div>
            </div>

            {/* Create/Edit Form */}
            {(isCreating || editingId) && (
                <div className="animate-fade-in-up border-2 rounded-lg p-6" style={{
                    borderColor: 'var(--glass-border)',
                    backgroundColor: 'var(--glass-bg)'
                }}>
                    <h3 className="text-lg font-bold mb-4" style={{ color: 'var(--text-primary)' }}>
                        {editingId ? 'Modifier la Session' : 'Nouvelle Session'}
                    </h3>
                    <div className="space-y-4">
                        <div>
                            <label className="block text-sm font-bold mb-2" style={{ color: 'var(--text-secondary)' }}>
                                Objectifs (un par ligne)
                            </label>
                            <textarea
                                value={formData.objectives}
                                onChange={(e) => setFormData({ ...formData, objectives: e.target.value })}
                                rows={3}
                                className="w-full border-2 rounded-lg px-4 py-2 focus:outline-none"
                                style={{
                                    backgroundColor: 'rgba(0,0,0,0.2)',
                                    borderColor: 'var(--border-color)',
                                    color: 'var(--text-primary)',
                                    outlineColor: 'var(--accent-color)'
                                }}
                                placeholder="- Créer le Version Manager&#10;- Intégrer dans l'admin&#10;- Tester la timeline"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-bold mb-2" style={{ color: 'var(--text-secondary)' }}>
                                Durée (minutes)
                            </label>
                            <input
                                type="number"
                                value={formData.duration}
                                onChange={(e) => setFormData({ ...formData, duration: e.target.value })}
                                className="w-full border-2 rounded-lg px-4 py-2 focus:outline-none"
                                style={{
                                    backgroundColor: 'rgba(0,0,0,0.2)',
                                    borderColor: 'var(--border-color)',
                                    color: 'var(--text-primary)',
                                    outlineColor: 'var(--accent-color)'
                                }}
                                placeholder="120"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-bold mb-2" style={{ color: 'var(--text-secondary)' }}>
                                Tâches Accomplies (une par ligne)
                            </label>
                            <textarea
                                value={formData.tasksCompleted}
                                onChange={(e) => setFormData({ ...formData, tasksCompleted: e.target.value })}
                                rows={3}
                                className="w-full border-2 rounded-lg px-4 py-2 focus:outline-none"
                                style={{
                                    backgroundColor: 'rgba(0,0,0,0.2)',
                                    borderColor: 'var(--border-color)',
                                    color: 'var(--text-primary)',
                                    outlineColor: 'var(--accent-color)'
                                }}
                                placeholder="- Fichiers de contexte créés&#10;- Composants React terminés"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-bold mb-2" style={{ color: 'var(--text-secondary)' }}>
                                Notes
                            </label>
                            <textarea
                                value={formData.notes}
                                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                                rows={4}
                                className="w-full border-2 rounded-lg px-4 py-2 focus:outline-none"
                                style={{
                                    backgroundColor: 'rgba(0,0,0,0.2)',
                                    borderColor: 'var(--border-color)',
                                    color: 'var(--text-primary)',
                                    outlineColor: 'var(--accent-color)'
                                }}
                                placeholder="Notes supplémentaires sur cette session..."
                            />
                        </div>

                        <div className="flex gap-2">
                            <button
                                onClick={() => (editingId ? handleUpdate(editingId) : handleCreate())}
                                className="px-6 py-2 rounded-lg font-bold"
                                style={{
                                    backgroundColor: 'var(--accent-color)',
                                    color: 'var(--bg-primary)'
                                }}
                            >
                                {editingId ? 'Mettre à jour' : 'Créer'}
                            </button>
                            <button
                                onClick={resetForm}
                                className="px-6 py-2 border-2 rounded-lg font-bold transition-colors"
                                style={{
                                    borderColor: 'var(--border-color)',
                                    color: 'var(--text-secondary)'
                                }}
                            >
                                Annuler
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Sessions List */}
            <div className="space-y-4">
                {sessions.length === 0 ? (
                    <div className="text-center py-12 border-2 border-dashed rounded-lg" style={{ borderColor: 'var(--border-color)' }}>
                        <Clock className="w-16 h-16 mx-auto mb-4" style={{ color: 'var(--text-secondary)' }} />
                        <p className="text-lg" style={{ color: 'var(--text-secondary)' }}>Aucune session enregistrée</p>
                        <p className="text-sm mt-2 opacity-70" style={{ color: 'var(--text-secondary)' }}>Créez votre première session pour commencer le suivi</p>
                    </div>
                ) : (
                    sessions.map((session) => (
                        <div key={session.id} className="border-l-4 rounded-lg p-4 shadow-sm" style={{
                            backgroundColor: 'var(--glass-bg)',
                            borderColor: 'var(--glass-border)',
                            borderLeftColor: '#3b82f6'
                        }}>
                            <div className="flex justify-between items-start mb-4">
                                <div>
                                    <h3 className="text-lg font-bold" style={{ color: 'var(--text-primary)' }}>
                                        Session du {new Date(session.date).toLocaleDateString('fr-FR', {
                                            day: 'numeric',
                                            month: 'long',
                                            year: 'numeric',
                                            hour: '2-digit',
                                            minute: '2-digit',
                                        })}
                                    </h3>
                                    {session.duration && (
                                        <p className="text-sm mt-1 flex items-center gap-1" style={{ color: 'var(--text-secondary)' }}>
                                            <Clock className="w-4 h-4" />
                                            {session.duration} minutes
                                        </p>
                                    )}
                                </div>
                                <div className="flex gap-2">
                                    <button
                                        onClick={() => startEdit(session)}
                                        className="p-2 transition-colors"
                                        style={{ color: '#3b82f6' }}
                                    >
                                        <Edit2 className="w-4 h-4" />
                                    </button>
                                    <button
                                        onClick={() => handleDelete(session.id)}
                                        className="p-2 transition-colors"
                                        style={{ color: '#ef4444' }}
                                    >
                                        <Trash2 className="w-4 h-4" />
                                    </button>
                                </div>
                            </div>

                            {/* Objectives */}
                            {session.objectives.length > 0 && (
                                <div className="mb-3">
                                    <h4 className="text-sm font-bold mb-2" style={{ color: 'var(--accent-glow)' }}>Objectifs</h4>
                                    <ul className="space-y-1">
                                        {session.objectives.map((obj, i) => (
                                            <li key={i} className="text-sm flex items-start gap-2" style={{ color: 'var(--text-secondary)' }}>
                                                <Circle className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: '#3b82f6' }} />
                                                <span>{obj}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}

                            {/* Tasks Completed */}
                            {session.tasksCompleted.length > 0 && (
                                <div className="mb-3">
                                    <h4 className="text-sm font-bold mb-2" style={{ color: '#10b981' }}>Tâches Accomplies</h4>
                                    <ul className="space-y-1">
                                        {session.tasksCompleted.map((task, i) => (
                                            <li key={i} className="text-sm flex items-start gap-2" style={{ color: 'var(--text-secondary)' }}>
                                                <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: '#10b981' }} />
                                                <span>{task}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}

                            {/* Notes */}
                            {session.notes && (
                                <div className="rounded-lg p-3 border" style={{
                                    backgroundColor: 'rgba(0,0,0,0.2)',
                                    borderColor: 'var(--border-color)'
                                }}>
                                    <h4 className="text-sm font-bold mb-1" style={{ color: 'var(--text-secondary)' }}>Notes</h4>
                                    <p className="text-sm whitespace-pre-wrap" style={{ color: 'var(--text-primary)' }}>{session.notes}</p>
                                </div>
                            )}
                        </div>
                    ))
                )}
            </div>
        </div>
    );
};
